import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileSpreadsheet,
  FileText,
  Loader2,
  RefreshCw,
  Percent,
  IndianRupee,
  AlertTriangle,
} from 'lucide-react';
import ListPageHeader from '../common/ListPageHeader.js';
import ListFilterPanel from '../common/ListFilterPanel.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert';
import SelectField from '../common/SelectField.js';
import { DashboardLayout, AmountCard } from '../common/page_component/index.js';
import { filterInputClass, formLabelClass, listPageWrapperClass } from '../common/uiClasses.js';
import { formatCurrency, fullCurrency } from '../../utils/formatCurrency.js';
import { useAuth } from '../../context/AuthContext.js';
import {
  hasExternalCommissionAccess,
  hasMlAgentCommissionAccess,
} from '../../utils/networkMode.js';
import reportService from '../../services/ReportService.js';
import contractPartyService from '../../services/contractPartyService.js';
import userService from '../../services/UserService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { formatHubBranchLabel, formatUserWithLocation, filterShipmentLocations, destinationTypeLabel } from '../../utils/destinationType.js';

function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload) return [];
  for (const key of ['users', 'hubs', 'location_hubs', 'data', 'results', 'items', 'parties']) {
    if (Array.isArray(payload[key])) return payload[key];
    if (payload.data && Array.isArray(payload.data[key])) return payload.data[key];
  }
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return fullCurrency(n);
}

/**
 * Full-page agent / shipment commission breakdown (replaces dashboard modal).
 * mode: 'internal' | 'external'
 */
export default function CommissionBreakdownPage({ mode = 'internal' }) {
  const { user } = useAuth();
  const isExternal = mode === 'external';

  const allowed = isExternal
    ? hasExternalCommissionAccess(user)
    : hasMlAgentCommissionAccess(user);

  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    agent_user_id: '',
    contract_party_id: '',
    location_hub_id: '',
    status: '',
  });

  const [refreshKey, setRefreshKey] = useState(0);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [agents, setAgents] = useState([]);
  const [parties, setParties] = useState([]);
  const [hubs, setHubs] = useState([]);

  const setF = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!user || !allowed) return undefined;
    const load = async () => {
      try {
        const [usersRes, partiesRes, hubsRes] = await Promise.all([
          userService.getAll(null, null, { all: true }).catch(() => []),
          !isExternal
            ? contractPartyService.getAllUnpaginated().catch(() => [])
            : Promise.resolve([]),
          !isExternal
            ? locationHubService.getAllHubs(null, null, { all: true }).catch(() => [])
            : Promise.resolve([]),
        ]);
        const users = toArray(usersRes);
        setAgents(
          users.filter((u) => {
            const role = typeof u.role === 'string' ? u.role : (u.role_name || u.role?.role_name || '');
            return role === 'Agent' && u.user_id;
          }),
        );
        setParties(toArray(partiesRes?.data ?? partiesRes));
        setHubs(toArray(hubsRes?.data ?? hubsRes));
      } catch {
        setAgents([]);
        setParties([]);
        setHubs([]);
      }
    };
    load();
    return undefined;
  }, [user, allowed, isExternal]);

  const cleanParams = useCallback((src) => {
    const p = {};
    Object.entries(src || {}).forEach(([k, v]) => {
      if (v === '' || v == null) return;
      if (isExternal && k === 'agent_user_id') {
        p.agent_user_ids = [String(v)];
        return;
      }
      if (isExternal && (k === 'contract_party_id' || k === 'location_hub_id' || k === 'status')) {
        return;
      }
      p[k] = String(v);
    });
    return p;
  }, [isExternal]);

  // Dynamic filters: reload whenever any filter changes (debounced for date typing)
  useEffect(() => {
    if (!allowed) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const type = isExternal ? 'external-commission' : 'commission';
        const res = await reportService.getReportData(type, cleanParams(filters));
        if (cancelled) return;
        if (res?.success === false) {
          Alert.error(res.error || 'Failed to load commission breakdown');
          setPayload(null);
          return;
        }
        setPayload(res?.data || res);
      } catch (err) {
        if (cancelled) return;
        Alert.error(err.response?.data?.error || 'Failed to load commission breakdown');
        setPayload(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [allowed, filters, cleanParams, isExternal, refreshKey]);

  const handleReset = () => {
    setFilters({
      from_date: '',
      to_date: '',
      agent_user_id: '',
      contract_party_id: '',
      location_hub_id: '',
      status: '',
    });
  };

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleExcel = async () => {
    setExcelLoading(true);
    try {
      await reportService.downloadExcel(
        isExternal ? 'external-commission' : 'commission',
        cleanParams(filters),
      );
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Excel export failed');
    } finally {
      setExcelLoading(false);
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      await reportService.downloadPdf(
        isExternal ? 'external-commission' : 'commission',
        cleanParams(filters),
      );
    } catch (err) {
      Alert.error(err.response?.data?.error || 'PDF export failed');
    } finally {
      setPdfLoading(false);
    }
  };

  const columns = payload?.columns || [];
  const rows = payload?.rows || [];
  const totals = payload?.totals || {};

  const summary = useMemo(() => {
    if (isExternal) {
      const earned = Number(totals.commission || 0);
      const paid = Number(totals.paid || 0);
      const due = Math.max(0, earned - paid);
      return {
        earned,
        paid,
        due: Number(totals.settlement != null && totals.settlement < 0 ? Math.abs(totals.settlement) : due),
        count: rows.length,
      };
    }
    return {
      earned: Number(totals.commission_amount || 0),
      paid: Number(totals.paid_amount || 0),
      due: Number(totals.unpaid_amount || 0),
      count: Number(totals.entry_count || rows.length || 0),
    };
  }, [totals, rows.length, isExternal]);

  /** Group table rows by agent (User Name column index 1) for per-user breakdown */
  const byAgent = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const name = Array.isArray(row) ? (row[1] || 'Unknown') : (row.agent_name || 'Unknown');
      const freight = Number(Array.isArray(row) ? row[6] : row.freight) || 0;
      const handling = Number(Array.isArray(row) ? row[7] : row.handling) || 0;
      const commission = Number(Array.isArray(row) ? row[8] : row.commission) || 0;
      const other = Number(Array.isArray(row) ? row[9] : row.other_charges) || 0;
      const toPay = Number(Array.isArray(row) ? row[10] : row.to_pay) || 0;
      const paid = Number(Array.isArray(row) ? row[11] : row.paid) || 0;
      const settlement = Number(Array.isArray(row) ? row[12] : row.settlement) || 0;
      const tds = Number(Array.isArray(row) ? row[13] : row.tds_amount) || 0;
      const net = Number(Array.isArray(row) ? row[14] : row.net_amount) || 0;
      const cur = map.get(name) || {
        name,
        entries: 0,
        freight: 0,
        handling: 0,
        commission: 0,
        other: 0,
        toPay: 0,
        paid: 0,
        settlement: 0,
        tds: 0,
        net: 0,
      };
      cur.entries += 1;
      cur.freight += freight;
      cur.handling += handling;
      cur.commission += commission;
      cur.other += other;
      cur.toPay += toPay;
      cur.paid += paid;
      cur.settlement += settlement;
      cur.tds += tds;
      cur.net += net;
      map.set(name, cur);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const hubsById = useMemo(() => {
    const map = {};
    (hubs || []).forEach((h) => {
      if (h?.id != null) {
        map[h.id] = h;
        map[String(h.id)] = h;
      }
    });
    return map;
  }, [hubs]);

  const hubOptions = filterShipmentLocations(hubs || []).map((h) => {
    const name = formatHubBranchLabel(h, hubsById) || h.location || h.name;
    const kind = destinationTypeLabel(h?.destination_type);
    return {
      value: h.id != null ? String(h.id) : '',
      label: kind ? `${name} (${kind})` : name,
    };
  });

  const agentOptions = agents
    .filter((a) => a.user_id)
    .map((a) => ({
      value: String(a.user_id),
      label: formatUserWithLocation(a, hubsById),
    }));

  const partyOptions = parties.map((p) => ({
    value: p.id != null ? String(p.id) : '',
    label: p.name || p.company_name || `Party ${p.id}`,
  }));

  if (!allowed) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center text-gray-500">
          Commission breakdown is not available for this company.
        </div>
      </DashboardLayout>
    );
  }

  const roleName = user?.role_name || user?.role?.role_name || user?.role;
  const title = isExternal ? 'External Commission Breakdown' : 'Agent Commission Breakdown';
  const backTo = roleName === 'Accountant'
    ? '/accountant-dashboard'
    : roleName === 'Owner'
      ? '/owner-dashboard'
      : '/admin-dashboard';

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
        <ListPageHeader
          title={title}
          icon={Percent}
          subtitle="Filters update live — export PDF / Excel anytime"
          backTo={backTo}
        >
          <button
            type="button"
            onClick={handleExcel}
            disabled={excelLoading}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold disabled:opacity-60 bg-emerald-50 border border-emerald-200 text-emerald-800"
          >
            {excelLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
            Excel
          </button>
          <button
            type="button"
            onClick={handlePdf}
            disabled={pdfLoading}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold disabled:opacity-60 bg-blue-50 border border-blue-200 text-blue-800"
          >
            {pdfLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            PDF
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold bg-gray-50 border border-gray-200 text-gray-700"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </ListPageHeader>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <AmountCard
            label="Total Agent Earned"
            amount={summary.earned}
            icon={<Percent className="w-5 h-5 text-white" />}
            color="bg-teal-600"
          />
          <AmountCard
            label="Amount Paid"
            amount={summary.paid}
            icon={<IndianRupee className="w-5 h-5 text-white" />}
            color="bg-green-600"
          />
          <AmountCard
            label="Amount Due"
            amount={summary.due}
            icon={<AlertTriangle className="w-5 h-5 text-white" />}
            color="bg-orange-500"
          />
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 relative min-h-[80px] sm:min-h-[90px] pr-11 sm:pr-14">
            <p className="text-gray-500 text-xs sm:text-sm font-medium">Entries</p>
            <p className="text-lg sm:text-xl font-bold text-gray-900 mt-1">{summary.count}</p>
            <div className="p-1.5 sm:p-2 absolute rounded-lg right-2 bottom-2 sm:right-3 sm:bottom-3 bg-indigo-500">
              <FileText className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <ListFilterPanel>
          {!isExternal && (
            <div className="min-w-0">
              <SelectField
                variant="filter"
                label="Contracted Party"
                labelClassName={formLabelClass}
                value={filters.contract_party_id}
                onChange={(e) => setF('contract_party_id', e.target.value)}
                placeholder="All Contracted Parties"
                emptyLabel="All Contracted Parties"
                options={[
                  { value: '', label: 'All Contracted Parties' },
                  ...partyOptions,
                ]}
              />
            </div>
          )}
          <div className="min-w-0">
            <SelectField
              variant="filter"
              label="Agent / User"
              labelClassName={formLabelClass}
              value={filters.agent_user_id}
              onChange={(e) => setF('agent_user_id', e.target.value)}
              placeholder="All Agents"
              emptyLabel="All Agents"
              options={[
                { value: '', label: 'All Agents' },
                ...agentOptions,
              ]}
            />
          </div>
          {!isExternal && (
            <div className="min-w-0">
              <SelectField
                variant="filter"
                label="Hub / Branch"
                labelClassName={formLabelClass}
                value={filters.location_hub_id}
                onChange={(e) => setF('location_hub_id', e.target.value)}
                placeholder="All Hubs"
                emptyLabel="All Hubs"
                options={[
                  { value: '', label: 'All Hubs' },
                  ...hubOptions,
                ]}
              />
            </div>
          )}
          {!isExternal && (
            <div className="min-w-0">
              <SelectField
                variant="filter"
                label="Status"
                labelClassName={formLabelClass}
                value={filters.status}
                onChange={(e) => setF('status', e.target.value)}
                placeholder="All Status"
                emptyLabel="All Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'UNPAID', label: 'Unpaid' },
                  { value: 'PAID', label: 'Paid' },
                ]}
              />
            </div>
          )}
          <div className="min-w-0">
            <label className={formLabelClass}>From date</label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => setF('from_date', e.target.value)}
              className={filterInputClass}
            />
          </div>
          <div className="min-w-0">
            <label className={formLabelClass}>To date</label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => setF('to_date', e.target.value)}
              className={filterInputClass}
            />
          </div>
          <div className="min-w-0 flex items-end col-span-2 md:col-span-1">
            <button
              type="button"
              onClick={handleReset}
              className="w-full rounded-xl border border-gray-300 text-gray-700 text-sm font-medium px-3 py-2 hover:bg-gray-50"
            >
              Clear filters
            </button>
          </div>
        </ListFilterPanel>

        {loading ? (
          <Loader fullScreen={false} message="Loading commission breakdown…" />
        ) : (
          <>
            {/* Per-user breakdown */}
            {byAgent.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 mb-4 sm:mb-6">
                <h2 className="text-sm sm:text-base font-semibold text-gray-800 mb-1">
                  Breakdown by Agent
                </h2>
                {!isExternal && (
                  <p className="text-xs text-gray-500 mb-3">
                    Settlement = To-Pay − Net commission (same as ML sheet "Net to remit"). Positive: agent remits to company. Negative: company owes the agent.
                  </p>
                )}
                {isExternal && <div className="mb-3" />}
                <div className="overflow-x-auto -mx-1">
                  <table className="min-w-full text-xs sm:text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="py-2 pr-3 font-medium">Agent</th>
                        <th className="py-2 pr-3 font-medium text-right">Entries</th>
                        <th className="py-2 pr-3 font-medium text-right">Freight</th>
                        <th className="py-2 pr-3 font-medium text-right">Commission</th>
                        <th className="py-2 pr-3 font-medium text-right">ToPay</th>
                        <th className="py-2 pr-3 font-medium text-right">Paid</th>
                        {!isExternal && (
                          <>
                            <th className="py-2 pr-3 font-medium text-right">TDS</th>
                            <th className="py-2 pr-3 font-medium text-right">Net</th>
                          </>
                        )}
                        <th className="py-2 font-medium text-right">Settlement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byAgent.map((a) => (
                        <tr
                          key={a.name}
                          className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            const match = agents.find((u) => {
                              const n = (u.name || u.username || '').trim().toLowerCase();
                              return n === String(a.name || '').trim().toLowerCase();
                            });
                            if (match?.user_id) {
                              setF('agent_user_id', String(match.user_id));
                            }
                          }}
                          title="Filter by this agent"
                        >
                          <td className="py-2 pr-3 font-medium text-gray-800">{a.name}</td>
                          <td className="py-2 pr-3 text-right text-gray-600">{a.entries}</td>
                          <td className="py-2 pr-3 text-right" title={fullCurrency(a.freight)}>
                            {formatCurrency(a.freight)}
                          </td>
                          <td className="py-2 pr-3 text-right text-teal-700 font-medium" title={fullCurrency(a.commission)}>
                            {formatCurrency(a.commission)}
                          </td>
                          <td className="py-2 pr-3 text-right" title={fullCurrency(a.toPay)}>
                            {formatCurrency(a.toPay)}
                          </td>
                          <td className="py-2 pr-3 text-right" title={fullCurrency(a.paid)}>
                            {formatCurrency(a.paid)}
                          </td>
                          {!isExternal && (
                            <>
                              <td className="py-2 pr-3 text-right" title={fullCurrency(a.tds)}>
                                {formatCurrency(a.tds)}
                              </td>
                              <td className="py-2 pr-3 text-right" title={fullCurrency(a.net)}>
                                {formatCurrency(a.net)}
                              </td>
                            </>
                          )}
                          <td
                            className={`py-2 text-right font-medium ${a.settlement < 0 ? 'text-rose-600' : 'text-gray-800'}`}
                            title={fullCurrency(a.settlement)}
                          >
                            {formatCurrency(a.settlement)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Detail table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-sm sm:text-base font-semibold text-gray-800">
                  Commission Entries
                </h2>
                <span className="text-xs text-gray-500">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
              </div>
              <div className="overflow-x-auto">
                {rows.length === 0 ? (
                  <p className="text-center text-sm text-gray-400 py-12">
                    No commission entries for the selected filters.
                  </p>
                ) : (
                  <table className="min-w-full text-xs sm:text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left text-gray-600">
                        {columns.map((col) => (
                          <th
                            key={col}
                            className="px-3 py-2.5 font-semibold whitespace-nowrap border-b border-gray-200"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => {
                        const cells = Array.isArray(row) ? row : columns.map((_, i) => row[i]);
                        return (
                          <tr
                            key={idx}
                            className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}
                          >
                            {cells.map((cell, cIdx) => {
                              const isMoney = cIdx >= 6;
                              const num = Number(cell);
                              const neg = isMoney && Number.isFinite(num) && num < 0;
                              return (
                                <td
                                  key={cIdx}
                                  className={`px-3 py-2 whitespace-nowrap border-b border-gray-100 ${
                                    isMoney ? 'text-right tabular-nums' : 'text-gray-800'
                                  } ${neg ? 'text-rose-600 font-medium' : ''}`}
                                  title={isMoney && Number.isFinite(num) ? fullCurrency(num) : undefined}
                                >
                                  {isMoney && Number.isFinite(num) ? money(num) : (cell ?? '—')}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                    {Object.keys(totals).length > 0 && (
                      <tfoot>
                        <tr className="bg-slate-100 font-semibold text-slate-800">
                          {columns.map((col, cIdx) => {
                            if (cIdx === 1) {
                              return (
                                <td key={col} className="px-3 py-2.5 whitespace-nowrap">
                                  TOTAL
                                </td>
                              );
                            }
                            const keyMap = {
                              6: isExternal ? 'freight' : 'freight',
                              7: 'handling',
                              8: isExternal ? 'commission' : 'commission_amount',
                              9: 'other_charges',
                              10: 'to_pay',
                              11: 'paid',
                              12: 'settlement',
                              13: 'tds_amount',
                              14: 'net_amount',
                            };
                            const key = keyMap[cIdx];
                            if (key && totals[key] != null) {
                              const n = Number(totals[key]);
                              return (
                                <td
                                  key={col}
                                  className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${n < 0 ? 'text-rose-600' : ''}`}
                                  title={fullCurrency(n)}
                                >
                                  {money(n)}
                                </td>
                              );
                            }
                            return <td key={col} className="px-3 py-2.5" />;
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
